import { Model } from 'libx'
import { observable, action, computed } from 'mobx'
import { serializable, serialize, snapshotable } from '../data/serialization'
import { validatable } from '../mixins/validation'
import { receiveErrors } from '../data/serverErrors'
import { validateObject } from 'utils/validx-validators'
import MutatorsBuilder from './MutatorsBuilder'

/**
 * Effect editor.
 */
@snapshotable
@validatable({ liveValidation: true })
export default class EffectEditor extends Model {
  /**
   * Convenience property to persist the expanded state of the accordion
   * in the UI.
   */
  @observable
  expanded = false

  /**
   * Mutators.
   */
  @observable
  @serializable
  mutators

  rules = {
    mutators: [validateObject('There are invalid mutators.')],
  }

  /**
   * Initializes the editor.
   */
  constructor(attrs, opts) {
    super()
    this.legendEditor = opts.legendEditor
    this.context = opts.context
    this.mutators = new MutatorsBuilder(this, this.legendEditor)
    this.set(attrs, opts)
  }

  /**
   * Determines if the editor controls are disabled.
   */
  @computed
  get editorDisabled() {
    return this.context.editorDisabled
  }

  @computed
  get isNumericQuestion() {
    return this.context.type === 'Numeric'
  }

  @computed
  get isSelectQuestion() {
    return this.context.type === 'Select'
  }

  /**
   * The symbol table to use for the Ryno Semantic Analyzer.
   */
  @computed
  get symbolTable() {
    return this.legendEditor.getSymbolTableFor(this.context)
  }

  /**
   * Hydrates props, returns those that were not used.
   */
  hydrate({ mutators, ...rest } = {}) {
    this.mutators.hydrate(mutators)
    return rest
  }

  /**
   * Serializes to json using the serializable attributes.
   */
  toJSON() {
    return serialize(this)
  }

  /**
   * Sets the expanded state.
   *
   * @param {boolean} value
   */
  @action.bound
  setExpanded(value) {
    this.expanded = value
  }

  /**
   * Receives errors.
   */
  receiveErrors(descriptor = {}) {
    receiveErrors(this, descriptor.inner)
  }
}

/**
 * Creates a new effect editor.
 */
function create(data, opts) {
  return new EffectEditor(data, opts)
}

EffectEditor.create = create

/**
 * Converts JSON data to an effect editor instance.
 */
function fromJS(data, opts) {
  return EffectEditor.create(data, {
    ...opts,
    parse: true,
    stripUndefined: true,
  })
}

EffectEditor.fromJS = fromJS
